/* @flow */
import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { reaction, observable, action, computed } from 'mobx';
import type { TranslatorProps } from 'react-i18next';
import { translate } from 'react-i18next';
import { Link } from 'react-router-dom';
import moment from 'moment';

import navigation from 'services/NavigationService';
import DashboardStore from 'stores/DashboardStore';
import type { WidgetItem } from 'stores/DashboardStore';
import { ComponentWithInjectedProps } from 'utils/injectedProps';
import { SORT_FIELD_PARAM, FILTERS_PARAM, SORT_DIRECTION_PARAM } from 'routes/contract';
import { isIE } from 'utils/browserDetection';
import { formulateUri } from 'tables/components/ReportView/state';
import { timeRangeToString } from 'stores/ReportViewStore';

import NivoBarChart from '../../components/NivoBarChart';
import FilterButtonGroup from '../../components/FilterButtonGroup';
import type { CommonWidgetProps } from '../../commonTypes';

import styles from 'views/Dashboard/Dashboard.module.scss';

const STATIC_DATA = {
  contracts: [
    {
      id: '5fce825c-a970-412a-baf1-29e0c4529621',
      er_contract_number: '48160315',
      account_id: '15764ccd-7433-4ae8-998a-10d728e864a7',
      account_name: 'Electro Rent',
      account_no: 'test123',
      assets_count: 3,
      day_completed: 15,
      day_remaining: 18,
      er_start_date: '2021-03-01',
      er_expected_end_date: '2021-03-29',
    },
    {
      id: 'c7dde389-4039-4e65-ac66-49069b2bdcd5',
      er_contract_number: '73862758',
      account_id: '15764ccd-7433-4ae8-998a-10d728e864a7',
      account_name: 'Electro Rent',
      account_no: 'test123',
      assets_count: 5,
      day_completed: 10,
      day_remaining: 30,
      er_start_date: '2021-03-01',
      er_expected_end_date: '2021-04-10',
    },
    {
      id: '51c6651b-4908-4f77-b721-888ac2f7e9c0',
      er_contract_number: '12460786',
      account_id: '15764ccd-7433-4ae8-998a-10d728e864a7',
      account_name: 'Electro Rent',
      account_no: 'test123',
      assets_count: 5,
      day_completed: 45,
      day_remaining: 20,
      er_start_date: '2021-03-01',
      er_expected_end_date: '2021-05-03',
    },
  ],
  contractsCount: 3,
};

export type ContractProps = CommonWidgetProps;

type InjectedProps = {
  dashboardStore: DashboardStore,
} & TranslatorProps;

@translate()
@inject('dashboardStore')
@observer
export default class Contracts extends ComponentWithInjectedProps<
  ContractProps,
  {},
  InjectedProps
> {
  static LOAD_THUMBNAIL = false;

  @computed get shouldLoadData(): boolean {
    const { size } = this.props;

    return size !== 'thumbnail' || Contracts.LOAD_THUMBNAIL;
  }

  @computed get widgetData(): { contracts: any[] | null, contractsCount: number } {
    const { dashboardStore } = this.props;

    if (!this.shouldLoadData) {
      return STATIC_DATA;
    }

    switch (this.widgetView) {
      case 'expired':
        return {
          contracts: dashboardStore.expiredContracts.contracts || null,
          contractsCount: dashboardStore.expiredContracts.contractsCount,
        };

      case 'due':
      default:
        return {
          contractsCount: dashboardStore.contracts.contractsCount || 0,
          contracts: dashboardStore.contracts.contracts || null,
        };
    }
  }

  @observable widgetView: 'due' | 'expired' = 'due';

  tradingAccountUpdateDisposer: Function;

  componentDidMount() {
    const { dashboardStore, widgetItem } = this.props;

    if (this.shouldLoadData) {
      dashboardStore.loadContracts(dashboardStore.loadedTradingAccount);
    }

    this.tradingAccountUpdateDisposer = reaction(
      () => ({
        ta: dashboardStore.loadedTradingAccount,
        widgetView: this.widgetView,
      }),
      ({ ta, widgetView }) => {
        if (this.shouldLoadData) {
          switch (widgetView) {
            case 'due':
              dashboardStore.loadContracts(ta);
              break;

            case 'expired':
              dashboardStore.loadExpiredContracts(ta);
              break;
          }
        }
      }
    );
  }

  componentWillUnmount() {
    this.tradingAccountUpdateDisposer();
  }

  render() {
    const { dashboardStore, t, size, widgetItem, isFullScreen } = this.props;
    const accountId = dashboardStore.loadedTradingAccount;
    let isLoading = false;
    let buttonLink = '';
    let buttonText = '';
    let optionsLabel = [t('general:dashboards.due_to_expire'), t('general:dashboards.overdue')];

    const { contracts, contractsCount } = this.widgetData;

    switch (this.widgetView) {
      case 'due': {
        isLoading = this.shouldLoadData && dashboardStore.loadingContracts;

        if (!isLoading) {
          optionsLabel[0] = `${t('general:dashboards.due_to_expire')} ${t(
            'general:dashboards.due_to_expire_total',
            { contractsCount }
          )}`;
        }

        const filters = [
          {
            key: 'er_status',
            values: ['open'],
          },
          {
            key: 'expected_end_date',
            values: [moment().format('YYYY-MM-DD')],
          },
        ];

        if (accountId)
          filters.push({
            key: 'account_id',
            values: [accountId],
          });

        buttonLink = formulateUri('/orders', {
          page: 1,
          filters,
          sort: {
            columnName: 'contract_due_to_expire',
            direction: 'ASC',
          },
        });

        buttonText = t('general:actions.view_all_contracts');
        break;
      }

      case 'expired': {
        isLoading = this.shouldLoadData && dashboardStore.loadingExpiredContracts;

        if (!isLoading) {
          optionsLabel[1] = `${t('general:dashboards.overdue')} ${t(
            'general:dashboards.overdue_total',
            { contractsCount }
          )}`;
        }

        const now = moment();
        const oneYearAgo = moment(now).subtract(1, 'year');
        const filters = [
          {
            key: 'er_status',
            values: ['open'],
            overrideImplicit: true,
          },
          {
            key: 'start_end_date',
            values: [timeRangeToString(oneYearAgo.toDate(), now.toDate())],
          },
        ];

        if (accountId)
          filters.push({
            key: 'account_id',
            values: [accountId],
          });

        buttonLink = formulateUri('/orders', {
          page: 1,
          filters,
          sort: {
            columnName: 'er_expected_end_date',
            direction: 'ASC',
          },
        });

        buttonText = t('general:actions.view_all_contracts');
        break;
      }
    }

    const contractData = contracts
      ? contracts
          .map((contract: any, index: number) => ({
            account_name: contract.account_name,
            account_no: contract.account_no,
            contract: contract.er_contract_number,
            contract_id: contract.id,
            er_start_date: contract.er_start_date,
            er_expected_end_date: contract.er_expected_end_date,
            'Start Date': contract.er_start_date,
            'End Date': contract.er_expected_end_date,
            'Days Completed': contract.day_completed,
            'Days CompletedColor': 'hsl(203, 54%, 46%)',
            'Days Remaining': contract.day_remaining,
            'Days RemainingColor': 'hsl(200, 92%, 79%)',
          }))
          .sort((a, b) => a['Days Remaining'] - b['Days Remaining'])
          .reverse()
      : [];

    return (
      <NivoBarChart
        widgetInfos={[
          {
            title: t('general:dashboards.contracts_due_to_expire_and_expired'),
            variant: 'default',
          },
        ]}
        tableTitles={[t('general:contract'), t('general:dashboards.abbrs.number_of_days')]}
        widgetItem={widgetItem}
        size={size}
        isFullScreen={isFullScreen}
        isIE={isIE}
        data={contractData}
        isLoading={isLoading}
        legendKeys={[
          {
            label: t('general:labels.days_completed'),
            color: 'hsl(203, 54%, 46%)',
          },
          {
            label: t('general:labels.days_remaining'),
            color: 'hsl(200, 92%, 79%)',
          },
        ]}
        button={!!buttonLink}
        buttonLink={buttonLink}
        buttonText={buttonText}
        // Nivo bar chart props
        indexBy="contract_id"
        keys={['Days Completed', 'Days Remaining']}
        colorBy={function(e: any): string {
          return e.data[e.id + 'Color'];
        }}
        onClick={e => {
          navigation.navigateTo(`/orders/${e.indexValue}`);
        }}
        axisLeft={{
          renderTick: tick => {
            const contractNo: string = contractData[tick.tickIndex]
              ? contractData[tick.tickIndex].contract
              : '';

            return (
              <g key={tick.key} transform={`translate(${tick.x},${tick.y})`}>
                <text textAnchor="end" alignmentBaseline="central" transform="translate(-12, 0)">
                  <Link to={`/orders/${tick.key}`} className={styles['widgetSVGLink']}>
                    {contractNo}
                  </Link>
                </text>
              </g>
            );
          },
        }}
        tooltip={function(data) {
          return (
            <div>
              <div>{`${data.data.account_name} - ${data.data.account_no}`}</div>
              <div>
                {`${data.id}:`} <b>{data.value}</b>
              </div>
              <div>
                {t('general:labels.start_date')}: {data.data.er_start_date}
                <br />
                {t('general:labels.expected_end_date')} : {data.data.er_expected_end_date}
              </div>
            </div>
          );
        }}
        footer={
          size !== 'thumbnail' && (
            <FilterButtonGroup
              value={this.widgetView}
              onOptionClick={option => {
                if (['due', 'expired'].includes(option.value)) {
                  // flow-disable-next-line
                  this.widgetView = option.value;
                }
              }}
              options={[
                { label: optionsLabel[0], value: 'due' },
                { label: optionsLabel[1], value: 'expired' },
              ]}
            />
          )
        }
      />
    );
  }
}
